/** @format */

import React, { memo } from 'react';

import { FormattedMessage } from 'gatsby-plugin-intl';
import moment from 'moment';
import styled from 'styled-components';

import { selectorBookingCommercialConditions, TReducerBooking } from '../../../../@ducks/booking/selectors';
import { TRoom } from '../../../../@ducks/room/declarations';
import { utilityGetRoomTitle } from '../../../../@ducks/room/helpers';
import { useRoomFeatures } from '../../../../@ducks/room/hooks/useRoomFeatures';
import PinIcon from '../../../../assets/icons/dovevivo_icon_pin.svg';
import { useAppSelector } from '../../../../redux/hooks';
import { mediaQuery } from '../../../../theme/mediaQuery';
import { utilityGetPrice } from '../../../../utils/helpers/utilityGetPrice';
import { UIColumn } from '../../../ui/Grid/Column';
import { UIRow } from '../../../ui/Grid/Row';
import { UIText } from '../../../ui/Text';
import { AvailableFrom } from './AvailableFrom';
import { DiscountPrice } from './DiscountPrice';
import { UILoaderCardRoom } from './Loader';

interface Props {
  locale: 'it' | 'en';
  room: TRoom;
  isLoading: boolean;
  availableFrom: string | undefined;
  hasInactiveURL?: boolean;
  availability?: TReducerBooking['availability'];
}

export const Content = memo(({ locale, room, hasInactiveURL, availability, availableFrom, isLoading }: Props) => {
  const { title } = utilityGetRoomTitle(locale, room.localizedData);
  const flatFeatures = useRoomFeatures({ room });
  const address = room.address && room.address.toLocaleLowerCase();
  const { name } = room.city;
  const showTags = room.flatFeatures.tags && room.flatFeatures.tags.length > 0;
  const description = room?.building?.nearTransportServices[0]?.description.toLowerCase() || '';
  const walkingDistance = room?.building?.nearTransportServices[0]?.walkingDistance
    ? `(${room?.building?.nearTransportServices[0]?.walkingDistance}m)`
    : '';
  const commercialConditions = useAppSelector(selectorBookingCommercialConditions);

  const { hasPromo, getDiscountedPrice, getBasePrice, getDiscountUntilDate } = utilityGetPrice({
    commercialConditions,
    availability,
    room,
  });
  const formattedDiscountUntilDate = moment(getDiscountUntilDate()).format('DD/MM/YYYY');

  return (
    <Container>
      <header>
        <Title>{title}</Title>
        <Address>
          <UIRow>
            <div>
              <img src={PinIcon} alt={room.address} style={{ marginRight: '0.625rem' }} />
            </div>
            <UIColumn fluid xs={11}>
              <span>{`${name} | ${address}`}</span>
              {(description || walkingDistance) && <span> |{` ${description} ${walkingDistance}`}</span>}
            </UIColumn>
          </UIRow>
        </Address>
        {showTags && (
          <Tags>
            {room.flatFeatures.tags.map(tag => (
              <Tag key={tag}>
                <FormattedMessage id={`flat_tag_${tag}`} />
              </Tag>
            ))}
          </Tags>
        )}
        <Features>{flatFeatures}</Features>
      </header>
      {!hasInactiveURL && (
        <FooterPriceWrapper>
          {hasPromo() ? (
            <DiscountPrice
              oldPrice={getBasePrice()}
              endPromo={formattedDiscountUntilDate}
              newPrice={getDiscountedPrice()}
              isCardRoom
            />
          ) : (
            <PriceContainer>
              <UIText text="card_price_from" intlFormatter />{' '}
              <Price>
                {getBasePrice()}
                <span>€</span>
              </Price>
              <UIText text="card_price_monthly" intlFormatter />
            </PriceContainer>
          )}
          {!isLoading ? (
            <AvailableFrom date={availableFrom} availability={availability} />
          ) : (
            <WrapperLoader>
              <UILoaderCardRoom />
            </WrapperLoader>
          )}
        </FooterPriceWrapper>
      )}
    </Container>
  );
});

// #region ::: COMPONENTS
const Container = styled.div`
  display: grid;
  height: 100%;

  header {
    height: auto;
  }
`;

const Tags = styled.div`
  display: flex;
`;

const Title = styled.h4`
  color: ${({ theme }) => theme.colors.joivy['teal-dark']};
  display: inline-block;
  font-size: 1.125rem;
  margin: 0 0 1rem;
`;

const Address = styled.h5`
  color: ${({ theme }) => theme.colors.joivy.black};
  font-family: ${({ theme }) => theme.font.body};
  font-size: 0.875rem;
  margin: 0 0 0.75rem;
  text-transform: capitalize;
  font-weight: 400;
`;

const Features = styled.h6`
  color: ${({ theme }) => theme.colors.joivy.black};
  font-family: ${({ theme }) => theme.font.body};
  font-size: 0.75rem;
  margin: 0;
  line-height: 1.6;
  font-weight: 400;
`;

const PriceContainer = styled.h4`
  font-family: ${({ theme }) => theme.font.body};
  font-size: 0.875rem;
  margin: 0;
  color: ${({ theme }) => theme.colors.joivy.black};
`;

const Price = styled.span`
  font-family: ${({ theme }) => theme.font.title};
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.joivy['teal-light']};

  span {
    font-size: 1rem;
    font-weight: 400;
  }
`;

const Tag = styled.div`
  font-family: ${({ theme }) => theme.font.body};
  font-size: 0.75rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.joivy.pink};
  background-color: transparent;
  padding: 6px 10px;
  border: 2px solid ${({ theme }) => theme.colors.pink};
  border-radius: 10px;
  text-transform: uppercase;
  font-weight: 700;
  margin-bottom: 0.438rem;
`;
const FooterPriceWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-flow: row wrap;
  row-gap: 0.25rem;
  align-items: flex-end;
  margin-top: 3.125rem;
  & > div {
    flex: 1;
  }
  ${mediaQuery.lg`
    margin-top: 0;
  `}
  ${mediaQuery.md`
    margin-top: 0;
  `}
`;

const WrapperLoader = styled.div`
  display: flex;
  justify-content: flex-end;
`;
// #endregion
